import type { FlatMappedGtmEventPayload, UserScopeData } from './gtm.types';
import type { UserDto } from '@/queries/user/user.schema';
import { USER_SCOPE_CACHE_KEY } from './constants';

const ISO_ALPHA_2_COUNTRY_CODE_REGEX = /^[A-Z]{2}$/;
const ISO_ALPHA_3_COUNTRY_CODE_REGEX = /^[A-Z]{3}$/;

const ALPHA3_TO_ALPHA2_COUNTRY_MAP: Record<string, string> = {
  USA: 'US',
  GBR: 'GB',
};

const LEGACY_COUNTRY_CODE_MAP: Record<string, string> = {
  UK: 'GB',
};

function normalizeCountryCode(country: string | null | undefined): string | undefined {
  if (!country) {
    return undefined;
  }

  const normalizedCountry = country.trim().toUpperCase();
  const normalizedLegacyCountry = LEGACY_COUNTRY_CODE_MAP[normalizedCountry] ?? normalizedCountry;

  if (ISO_ALPHA_2_COUNTRY_CODE_REGEX.test(normalizedLegacyCountry)) {
    return normalizedLegacyCountry;
  }

  if (ISO_ALPHA_3_COUNTRY_CODE_REGEX.test(normalizedLegacyCountry)) {
    return ALPHA3_TO_ALPHA2_COUNTRY_MAP[normalizedLegacyCountry];
  }

  return undefined;
}

function isClientSide(): boolean {
  return typeof window !== 'undefined';
}

function sanitize(user: UserScopeData): UserScopeData {
  const sanitizedEntries = Object.entries(user).filter(([, value]) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  });

  return Object.fromEntries(sanitizedEntries);
}

function extractStringField(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toUserScopeData(user: Partial<UserDto>): UserScopeData {
  return {
    external_id: typeof user.id === 'string' ? user.id : undefined,
    email: typeof user.email === 'string' ? user.email : undefined,
    first_name: user.firstName ?? undefined,
    last_name: user.lastName ?? undefined,
    phone: user.phone ?? undefined,
    country: normalizeCountryCode(user.country),
    state: user.region ?? undefined,
  };
}

function cache(user: Partial<UserDto>): void {
  if (!isClientSide()) {
    return;
  }
  const sanitizedUser = sanitize(toUserScopeData(user));

  if (!sanitizedUser.external_id || !sanitizedUser.email) {
    return;
  }

  try {
    window.localStorage.setItem(USER_SCOPE_CACHE_KEY, JSON.stringify(sanitizedUser));
  } catch {
    // Ignore storage errors
  }
}

function clear(): void {
  if (!isClientSide()) {
    return;
  }

  try {
    window.localStorage.removeItem(USER_SCOPE_CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
}

function readCached(): UserScopeData | null {
  if (!isClientSide()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(USER_SCOPE_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as UserScopeData;
    const sanitized = sanitize(parsed);

    if (!Object.keys(sanitized).length) {
      return null;
    }

    return sanitized;
  } catch {
    return null;
  }
}

function toAnalyticsUserFields(user?: UserScopeData): Partial<FlatMappedGtmEventPayload> {
  if (!user) {
    return {};
  }

  const sanitizedUser = sanitize(user);

  return {
    email: extractStringField(sanitizedUser.email),
    phone: extractStringField(sanitizedUser.phone),
    external_id: extractStringField(sanitizedUser.external_id),
    first_name: extractStringField(sanitizedUser.first_name),
    last_name: extractStringField(sanitizedUser.last_name),
    state: extractStringField(sanitizedUser.state),
  };
}

function resolveAnalyticsUserFields(user?: Partial<UserDto>): Partial<FlatMappedGtmEventPayload> {
  const passedUser = user ? sanitize(toUserScopeData(user)) : {};
  const resolvedUser = Object.keys(passedUser).length > 0
    ? passedUser
    : (readCached() ?? undefined);
  return toAnalyticsUserFields(resolvedUser);
}

export const gtmUserProfileStore = {
  cache,
  clear,
  resolveAnalyticsUserFields,
};
